import {
  Col,
  Container,
  Form,
  OverlayTrigger,
  Row,
  Table,
  Tooltip,
} from 'react-bootstrap'
import Navbar from '../../components/navbar/navbar'
import React, { useState, useCallback, useMemo } from 'react'
import { endOfMonth, format, startOfMonth } from 'date-fns'
import { CustomInputDate } from '../../components/CustomInputDate'
import { CustomButton } from '../../components/CustomButton'
import { MagnifyingGlass } from 'phosphor-react'
import { sendErrorApiMessage } from '../../utils/sendErrorApiMessage'
import apinode from '../../services/apinode'
import { unifyParams } from '../../utils/unifyParams'
import { formatPrice } from '../../utils/formatPrice'
import { EyeFill, PrinterFill } from 'react-bootstrap-icons'
import { useOrdenation } from '../../hooks/useOrdenation'
import pdfMake from 'pdfmake/build/pdfmake'

const styles = `
.rdp-root {
  --rdp-font: 'Poppins', sans-serif;
  --rdp-accent-color: #2563eb;
  --rdp-bg: #f3f3f3;
  --rdp-border: #e5e7eb;
  --rdp-text: #1f2937;
  --rdp-muted: #6b7280;
  --rdp-light: #f8fafc;
  --rdp-shadow: 0 10px 25px -5px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05);
  font-family: var(--rdp-font);
  background: var(--rdp-bg);
  color: var(--rdp-text);
  min-height: 100vh;
}

.rdp-page {
  font-family: 'Poppins', sans-serif;
  background: #f3f3f3;
  color: #1f2937;
  min-height: 100vh;
  padding-bottom: 64px;
}

.rdp-hero {
  padding: 48px 0 32px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 36px;
}

.rdp-hero-label {
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #2563eb;
  margin-bottom: 10px;
}

.rdp-hero-title {
  font-family: 'Poppins', sans-serif;
  font-size: clamp(28px, 4vw, 44px);
  font-weight: 800;
  color: #1f2937;
  line-height: 1.1;
  margin: 0;
}

.rdp-hero-title span {
  color: #1d4ed8;
}

/* Filter card */
.rdp-filter-card {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  padding: 32px 32px 28px;
  margin-bottom: 40px;
  position: relative;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.rdp-filter-card:hover {
  box-shadow: var(--rdp-shadow);
  transform: translateY(-2px);
}

.rdp-filter-card::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: linear-gradient(90deg, #2563eb, #3b82f6, #60a5fa);
}

.rdp-filter-label {
  font-family: 'Poppins', sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 8px;
  display: block;
}

.rdp-input {
  background: #f3f3f3 !important;
  border: 2px solid #e5e7eb !important;
  border-radius: 12px !important;
  color: #1f2937 !important;
  font-family: 'Poppins', sans-serif !important;
  font-size: 14px !important;
  padding: 12px 16px !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
  height: auto !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.rdp-input:hover {
  border-color: #cbd5e1 !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

.rdp-input:focus {
  border-color: #2563eb !important;
  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.15) !important;
  outline: none !important;
  background: #f3f3f3 !important;
  transform: translateY(-1px);
}

.rdp-input::placeholder {
  color: #9ca3af !important;
}

/* Search button */
.rdp-btn-search {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important;
  border: none !important;
  border-radius: 12px !important;
  color: #f3f3f3 !important;
  font-family: 'Poppins', sans-serif !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  letter-spacing: 0.04em !important;
  padding: 12px 24px !important;
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
  white-space: nowrap !important;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.3);
}

.rdp-btn-search:hover {
  background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%) !important;
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px rgba(37, 99, 235, 0.4) !important;
}

.rdp-btn-search:active {
  transform: translateY(-1px) !important;
}

/* Stats bar */
.rdp-stats {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 24px;
  padding: 20px;
  background: linear-gradient(90deg, #f8fafc 0%, #f3f3f3 100%);
  border-radius: 16px;
  border: 1px solid #e5e7eb;
}

.rdp-stats-count {
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
}

.rdp-stats-count strong {
  color: #1f2937;
  font-size: 18px;
  margin-right: 6px;
}

.rdp-divider {
  height: 24px;
  width: 1px;
  background: #e5e7eb;
}

/* Main table */
.rdp-table-wrap {
  background: #f3f3f3;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 28px;
  box-shadow: var(--rdp-shadow);
}

.rdp-table {
  margin: 0 !important;
  font-family: 'Poppins', sans-serif !important;
  font-size: 14px !important;
  color: #1f2937 !important;
}

.rdp-table thead tr {
  background: linear-gradient(90deg, #f8fafc 0%, #f3f3f3 100%) !important;
  border-bottom: 2px solid #e5e7eb !important;
}

.rdp-table thead th {
  font-family: 'Poppins', sans-serif !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.14em !important;
  text-transform: uppercase !important;
  color: #6b7280 !important;
  padding: 18px 20px !important;
  border: none !important;
  white-space: nowrap !important;
  vertical-align: middle !important;
  cursor: pointer !important;
  user-select: none !important;
  background: transparent !important;
  transition: all 0.2s ease !important;
  position: relative;
}

.rdp-table thead th:hover {
  color: #2563eb !important;
}

.rdp-table thead th::after {
  content: '';
  position: absolute;
  bottom: 8px;
  left: 20px;
  right: 20px;
  height: 2px;
  background: #2563eb;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.rdp-table thead th:hover::after {
  opacity: 0.3;
}

.rdp-table tbody tr {
  border-bottom: 1px solid #f1f5f9 !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
  background: transparent !important;
}

.rdp-table tbody tr:last-child {
  border-bottom: none !important;
}

.rdp-table tbody tr:hover {
  background: linear-gradient(90deg, rgba(37, 99, 235, 0.03) 0%, rgba(59, 130, 246, 0.02) 100%) !important;
  transform: scale(1.001);
}

.rdp-table tbody td {
  padding: 18px 20px !important;
  border: none !important;
  vertical-align: middle !important;
  background: transparent !important;
  color: #1f2937 !important;
}

/* Stripped rows */
.rdp-table.table-striped > tbody > tr:nth-of-type(odd) > * {
  --bs-table-accent-bg: rgba(248, 250, 252, 0.8) !important;
  color: #1f2937 !important;
}

.rdp-table.table-striped > tbody > tr:nth-of-type(even) > * {
  --bs-table-accent-bg: transparent !important;
  color: #1f2937 !important;
}

/* Value badge */
.rdp-value {
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  color: #10b981;
  font-size: 14px;
}

/* Qty badges */
.rdp-qty {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 12px;
  min-width: 32px;
  height: 28px;
  border-radius: 8px;
  padding: 0 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.rdp-qty-total {
  background: rgba(37, 99, 235, 0.15);
  color: #2563eb;
  border: 1px solid rgba(37, 99, 235, 0.3);
}

.rdp-qty-returned {
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

/* Order number */
.rdp-order-num {
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  font-size: 15px;
  color: #1f2937;
}

/* CPF text */
.rdp-cpf {
  font-size: 12px;
  color: #6b7280;
  font-family: 'Poppins', monospace;
  letter-spacing: 0.03em;
  font-weight: 500;
}

/* Action buttons */
.rdp-action-btn {
  background: #f8fafc !important;
  border: 2px solid #e5e7eb !important;
  border-radius: 10px !important;
  color: #6b7280 !important;
  padding: 8px 12px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
  font-size: 14px !important;
  font-weight: 600;
  gap: 4px;
  min-width: 44px;
  height: 44px;
}

.rdp-action-btn:hover {
  background: #2563eb !important;
  border-color: #2563eb !important;
  color: #f3f3f3 !important;
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 20px rgba(37, 99, 235, 0.3) !important;
}

.rdp-action-btn.active {
  background: rgba(37, 99, 235, 0.15) !important;
  border-color: #2563eb !important;
  color: #2563eb !important;
}

.rdp-action-btn-print:hover {
  background: #059669 !important;
  border-color: #059669 !important;
  box-shadow: 0 8px 20px rgba(5, 150, 105, 0.3) !important;
}

/* Sub-table (produtos) */
.rdp-subtable-row td {
  padding: 0 !important;
  background: #f8fafc !important;
}

.rdp-subtable-inner {
  border-top: 4px solid #2563eb;
  padding: 24px;
  animation: rdp-slide-down 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes rdp-slide-down {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}

.rdp-subtable-title {
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #2563eb;
  margin-bottom: 16px;
}

.rdp-subtable {
  margin: 0 !important;
  font-family: 'Poppins', sans-serif !important;
  font-size: 13px !important;
  color: #374151 !important;
  background: #f3f3f3 !important;
  border-radius: 16px !important;
  overflow: hidden !important;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
}

.rdp-subtable thead th {
  font-family: 'Poppins', sans-serif !important;
  font-size: 10px !important;
  font-weight: 700 !important;
  letter-spacing: 0.13em !important;
  text-transform: uppercase !important;
  color: #6b7280 !important;
  padding: 14px 16px !important;
  border: none !important;
  border-bottom: 2px solid #e5e7eb !important;
  background: #f8fafc !important;
}

.rdp-subtable tbody td {
  padding: 14px 16px !important;
  border: none !important;
  border-bottom: 1px solid #f1f5f9 !important;
  background: transparent !important;
  color: #4b5563 !important;
  vertical-align: middle !important;
}

.rdp-subtable tbody tr:last-child td {
  border-bottom: none !important;
}

.rdp-subtable .rdp-sku {
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.05em;
}

.rdp-subtable .rdp-date-tag {
  font-size: 11px;
  color: #6b7280;
  background: #f1f5f9;
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: 500;
}

/* Pagination */
.rdp-paginate {
  display: flex;
  justify-content: flex-end;
  padding: 24px 0;
}

.rdp-paginate .pagination .page-item .page-link {
  background: #f8fafc !important;
  border-color: #e5e7eb !important;
  color: #6b7280 !important;
  font-family: 'Poppins', sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  border-radius: 10px !important;
  margin: 0 2px;
  padding: 10px 14px !important;
  transition: all 0.2s ease !important;
}

.rdp-paginate .pagination .page-item.active .page-link {
  background: #2563eb !important;
  border-color: #2563eb !important;
  color: #f3f3f3 !important;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
}

.rdp-paginate .pagination .page-item .page-link:hover {
  background: #eff6ff !important;
  color: #2563eb !important;
  border-color: #93c5fd !important;
  transform: translateY(-1px);
}

/* Empty state */
.rdp-empty {
  text-align: center;
  padding: 100px 40px;
  color: #9ca3af;
}

.rdp-empty-icon {
  font-size: 64px;
  margin-bottom: 24px;
  opacity: 0.3;
}

.rdp-empty-text {
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #9ca3af;
  margin-bottom: 8px;
}

.rdp-empty-subtitle {
  font-size: 14px;
  color: #d1d5db;
}
`

export function RelatorioDevolucaoProdutos() {
  const [pedido, setPedido] = useState('')
  const [produto, setProduto] = useState('')
  const [date, setDate] = useState({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date()),
  })

  const [isLoading, setIsLoading] = useState(false)
  const [devolucoes, setDevolucoes] = useState([])
  const [produtos, setProdutos] = useState({})

  const generatePDF = (dadosPedido, itensDevolvidos) => {
    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [40, 40, 40, 40],
      content: [
        // HEADER COM DEGRADÊ SIMULADO
        {
          columns: [
            {
              stack: [
                {
                  text: 'RELATÓRIO DE DEVOLUÇÃO DE PRODUTOS',
                  style: 'overline',
                },
                { text: `Pedido #${dadosPedido.pedido}`, style: 'headerTitle' },
              ],
            },
            {
              stack: [
                { text: 'EMITIDO EM', style: 'overline', alignment: 'right' },
                {
                  text: `${format(new Date(), "dd/MM/yyyy 'às' HH:mm")} por ${localStorage.username}`,
                  style: 'dateText',
                  alignment: 'right',
                },
              ],
            },
          ],
        },

        {
          canvas: [
            { type: 'rect', x: 0, y: 10, w: 515, h: 1, color: '#e5e7eb' },
          ],
        },

        // CARD DE INFORMAÇÕES DO CLIENTE
        {
          margin: [0, 20, 0, 20],
          table: {
            widths: ['*'],
            body: [
              [
                {
                  fillColor: '#f8fafc',
                  padding: [15, 15, 15, 15],
                  stack: [
                    {
                      columns: [
                        {
                          stack: [
                            { text: 'CLIENTE', style: 'cardLabel' },
                            {
                              text: dadosPedido.cliente.toUpperCase(),
                              style: 'cardValue',
                            }, //
                          ],
                        },
                        {
                          stack: [
                            { text: 'CPF / CNPJ', style: 'cardLabel' },
                            { text: dadosPedido.cpf, style: 'cardValue' }, //
                          ],
                        },
                        {
                          stack: [
                            { text: 'VALOR DO PEDIDO', style: 'cardLabel' },
                            {
                              text: formatPrice(dadosPedido.valor),
                              style: 'cardValueHighlight',
                            }, //
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            ],
          },
          layout: 'noBorders',
        },

        // RESUMO DE QUANTIDADES
        {
          columns: [
            {
              text: [
                { text: 'Total de Itens: ', style: 'statsLabel' },
                { text: dadosPedido.produtos, style: 'statsValue' }, //[cite: 1]
              ],
            },
            {
              text: [
                { text: 'Itens Devolvidos: ', style: 'statsLabel' },
                {
                  text: dadosPedido.produtos_devolvidos,
                  style: 'statsValueReturned',
                }, //[cite: 1]
              ],
              alignment: 'right',
            },
          ],
        },

        {
          text: 'DETALHAMENTO DOS PRODUTOS',
          style: 'sectionTitle',
          margin: [0, 20, 0, 10],
        },

        // TABELA DE PRODUTOS ESTILIZADA
        {
          table: {
            headerRows: 1,
            widths: ['auto', '*', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: 'SKU', style: 'tHeader' },
                { text: 'DESCRIÇÃO', style: 'tHeader' },
                {
                  text: 'QTD',
                  style: 'tHeader',
                  alignment: 'center',
                },
                {
                  text: 'QTD DEV.',
                  style: 'tHeader',
                  alignment: 'center',
                },
                {
                  text: 'ÚLT. DEV.',
                  style: 'tHeader',
                  alignment: 'right',
                },
              ],
              ...itensDevolvidos.map((item, i) => [
                {
                  text: item.codigo,
                  style: 'tRow',
                  fillColor: i % 2 === 0 ? '#f3f3f3' : '#fcfcfc',
                }, //[cite: 1]
                {
                  text: item.descricao,
                  style: 'tRow',
                  fillColor: i % 2 === 0 ? '#f3f3f3' : '#fcfcfc',
                }, //[cite: 1]
                {
                  text: item.quantidade_nova,
                  style: 'tRowQty',
                  fillColor: i % 2 === 0 ? '#f3f3f3' : '#fcfcfc',
                }, //[cite: 1]
                {
                  text: item.quantidade_devolucao,
                  style: 'tRowQty',
                  fillColor: i % 2 === 0 ? '#f3f3f3' : '#fcfcfc',
                }, //[cite: 1]
                {
                  text: item.data
                    ? format(new Date(item.data), "dd/MM/yyyy 'às' HH:mm")
                    : 'N/I', //[cite: 1]
                  style: 'tRowDate',
                  fillColor: i % 2 === 0 ? '#f3f3f3' : '#fcfcfc',
                },
              ]),
            ],
          },
          layout: {
            hLineWidth: (i, node) =>
              i === 0 || i === node.table.body.length ? 0 : 1,
            vLineWidth: () => 0,
            hLineColor: () => '#eeeeee',
            paddingLeft: () => 10,
            paddingRight: () => 10,
            paddingTop: () => 8,
            paddingBottom: () => 8,
          },
        },

        // RODAPÉ DO DOCUMENTO
        {
          text: 'Este documento é um registro oficial de devolução de mercadorias.',
          style: 'footer',
          margin: [0, 40, 0, 0],
          alignment: 'center',
        },
      ],
      styles: {
        overline: {
          fontSize: 8,
          color: '#2563eb',
          bold: true,
          letterSpacing: 1.5,
        },
        headerTitle: {
          fontSize: 22,
          bold: true,
          color: '#111827',
          margin: [0, 2, 0, 0],
        },
        dateText: { fontSize: 10, color: '#6b7280', margin: [0, 2, 0, 0] },
        cardLabel: {
          fontSize: 8,
          color: '#6b7280',
          bold: true,
          margin: [0, 0, 0, 4],
        },
        cardValue: { fontSize: 11, bold: true, color: '#1f2937' },
        cardValueHighlight: { fontSize: 12, bold: true, color: '#059669' },
        statsLabel: { fontSize: 10, color: '#4b5563' },
        statsValue: { fontSize: 10, bold: true, color: '#1f2937' },
        statsValueReturned: { fontSize: 10, bold: true, color: '#dc2626' },
        sectionTitle: {
          fontSize: 10,
          bold: true,
          color: '#111827',
          letterSpacing: 0.5,
        },
        tHeader: {
          fontSize: 8,
          bold: true,
          color: '#9ca3af',
          margin: [0, 5, 0, 5],
        },
        tRow: { fontSize: 9, color: '#374151' },
        tRowQty: {
          fontSize: 9,
          bold: true,
          color: '#111827',
          alignment: 'center',
        },
        tRowDate: { fontSize: 8, color: '#6b7280', alignment: 'right' },
        footer: { fontSize: 8, color: '#9ca3af', italic: true },
      },
    }

    pdfMake.createPdf(docDefinition).open()
  }

  const handleGetProducts = useCallback(async (pedido, index) => {
    try {
      setProdutos((prevState) => ({
        [index]: {
          ...prevState[index],
          loading: true,
        },
      }))

      const response = await apinode.get(
        `/pedido-devolucoes/relatorio/produtos/${pedido}/${localStorage.idconta}`,
      )

      setProdutos((prevState) => ({
        [index]: {
          ...prevState[index],
          data: response.data,
        },
      }))

      return response.data
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao buscar produtos desse pedido')
    } finally {
      setProdutos((prevState) => ({
        [index]: {
          ...prevState[index],
          loading: false,
        },
      }))
    }
  }, [])

  async function handleGenerateReport(devolucao) {
    const response = await apinode.get(
      `/pedido-devolucoes/relatorio/produtos/${devolucao.pedido}/${localStorage.idconta}`,
    )

    const products = response.data

    if (products && products.length > 0) {
      generatePDF(devolucao, products)
    } else {
      alert('Não há produtos para gerar o relatório ou falha na busca.')
    }
  }

  const handleSearch = useCallback(async () => {
    try {
      setIsLoading(true)
      const url = unifyParams(
        `/pedido-devolucoes/relatorio/${localStorage.idconta}`,
        [
          { key: 'pedido', value: pedido },
          { key: 'produto', value: produto },
          { key: 'date', value: JSON.stringify(date) },
        ],
      )

      const response = await apinode.get(url)
      setDevolucoes(response.data)
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao buscar devoluções')
    } finally {
      setIsLoading(false)
    }
  }, [pedido, produto, date])

  const onChangeDate = useCallback((dates) => {
    const [start, end] = dates
    setDate({ from: start, to: end })
  }, [])

  const { filteredItems, Th, Paginate } = useOrdenation(
    devolucoes,
    'pedido',
    'desc',
  )

  const totalProdutosDevolvidos = useMemo(
    () =>
      filteredItems.reduce((acc, d) => acc + (d.produtos_devolvidos || 0), 0),
    [filteredItems],
  )

  return (
    <>
      <style>{styles}</style>
      <Navbar />

      <div className="rdp-page rdp-root">
        <Container>
          {/* Hero */}
          <div className="rdp-hero">
            <div className="text-center lg:text-left">
              <p className="rdp-hero-label">Relatórios</p>
              <h1 className="rdp-hero-title">
                Devolução de <span>Produtos</span>
              </h1>
            </div>
          </div>

          {/* Filter card */}
          <div className="rdp-filter-card">
            <Row className="align-items-end g-3">
              <Col
                xs={12}
                sm={6}
                lg={3}
              >
                <label className="rdp-filter-label">Pedido</label>
                <Form.Control
                  className="rdp-input w-100"
                  value={pedido}
                  onChange={(e) => setPedido(e.target.value)}
                  placeholder="Ex: 9743"
                />
              </Col>
              <Col
                xs={12}
                sm={6}
                lg={3}
              >
                <label className="rdp-filter-label">Produto</label>
                <Form.Control
                  className="rdp-input w-100"
                  value={produto}
                  onChange={(e) => setProduto(e.target.value)}
                  placeholder="SKU ou Descrição"
                />
              </Col>
              <Col
                xs={12}
                sm={6}
                lg={4}
              >
                <label className="rdp-filter-label">Período</label>
                <CustomInputDate
                  selected={date.from}
                  onChange={onChangeDate}
                  startDate={date.from}
                  endDate={date.to}
                  selectsRange
                  className="rdp-input w-100"
                />
              </Col>
              <Col
                xs={12}
                sm={6}
                lg={2}
              >
                <CustomButton
                  noMargin
                  title="Buscar"
                  onClick={handleSearch}
                  isLoading={isLoading}
                  isLoadingText="Buscando..."
                  className="rdp-btn-search w-100"
                >
                  <MagnifyingGlass
                    size={16}
                    weight="bold"
                  />
                </CustomButton>
              </Col>
            </Row>
          </div>

          {/* Results */}
          {filteredItems.length > 0 ? (
            <>
              {/* Stats bar */}
              <div className="rdp-stats">
                <span className="rdp-stats-count">
                  <strong
                    style={{
                      color: '#1d4ed8',
                    }}
                  >
                    {filteredItems.length}
                  </strong>
                  {filteredItems.length === 1
                    ? ' devolução encontrada'
                    : ' devoluções encontradas'}
                </span>

                <div className="rdp-divider"></div>
                <span className="rdp-stats-count">
                  <strong
                    style={{
                      color: '#1d4ed8',
                    }}
                  >
                    {totalProdutosDevolvidos}
                  </strong>{' '}
                  produtos devolvidos no total
                </span>
              </div>

              {/* Main table */}
              <div className="rdp-table-wrap">
                <Table className="rdp-table mb-0">
                  <thead>
                    <tr>
                      <Th label="Pedido" />
                      <Th label="Cliente" />
                      <Th label="Valor" />
                      <Th label="CPF/CNPJ" />
                      <Th label="Tot. Produtos" />
                      <Th label="Tot. Devolvidos" />
                      <Th label="Ações" />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((dev, index) => (
                      <React.Fragment key={`row-${index}`}>
                        <tr>
                          <td>
                            <span className="rdp-order-num">#{dev.pedido}</span>
                          </td>
                          <td>{dev.cliente}</td>
                          <td>
                            <span className="rdp-value">
                              {formatPrice(dev.valor)}
                            </span>
                          </td>
                          <td>
                            <span className="rdp-cpf">{dev.cpf}</span>
                          </td>
                          <td>
                            <span className="rdp-qty rdp-qty-total">
                              {dev.produtos}
                            </span>
                          </td>
                          <td>
                            <span className="rdp-qty rdp-qty-returned">
                              {dev.produtos_devolvidos}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <OverlayTrigger
                                overlay={
                                  <Tooltip id="tooltip-visualizar">
                                    Visualizar produtos
                                  </Tooltip>
                                }
                              >
                                <CustomButton
                                  noMargin
                                  className={`rdp-action-btn ${produtos[index] ? 'active' : ''}`}
                                  onClick={() =>
                                    produtos[index]
                                      ? setProdutos({})
                                      : handleGetProducts(dev.pedido, index)
                                  }
                                  disabled={produtos[index]?.loading}
                                >
                                  <EyeFill />
                                </CustomButton>
                              </OverlayTrigger>
                              <OverlayTrigger
                                overlay={
                                  <Tooltip id="tooltip-relatorio">
                                    Gerar Relatório
                                  </Tooltip>
                                }
                              >
                                <CustomButton
                                  noMargin
                                  className="rdp-action-btn rdp-action-btn-print"
                                  onClick={() => handleGenerateReport(dev)}
                                >
                                  <PrinterFill />
                                </CustomButton>
                              </OverlayTrigger>
                            </div>
                          </td>
                        </tr>
                        {produtos[index]?.data?.length > 0 && (
                          <tr className="rdp-subtable-row">
                            <td colSpan={7}>
                              <div className="rdp-subtable-inner">
                                <p className="rdp-subtable-title">
                                  Produtos do pedido #{dev.pedido}
                                </p>
                                <Table className="rdp-subtable mb-0">
                                  <thead>
                                    <tr>
                                      <th>SKU</th>
                                      <th>Descrição</th>
                                      <th>Quantidade</th>
                                      <th>Qtd. Devolvida</th>
                                      <th>Data Devolução</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {produtos[index].data.map((prod, pi) => (
                                      <tr key={pi}>
                                        <td>
                                          <span className="rdp-sku">
                                            {prod.codigo}
                                          </span>
                                        </td>
                                        <td>{prod.descricao}</td>
                                        <td>
                                          <span className="rdp-qty rdp-qty-total">
                                            {prod.quantidade_nova}
                                          </span>
                                        </td>
                                        <td>
                                          <span className="rdp-qty rdp-qty-returned">
                                            {prod.quantidade_devolucao}
                                          </span>
                                        </td>
                                        <td>
                                          <span className="rdp-date-tag">
                                            {prod.data
                                              ? format(
                                                  new Date(prod.data),
                                                  "dd/MM/yyyy 'às' HH:mm",
                                                )
                                              : 'N/I'}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="rdp-paginate">
                <Paginate />
              </div>
            </>
          ) : !isLoading && devolucoes.length === 0 ? (
            <div className="rdp-empty">
              <div className="rdp-empty-icon">
                <MagnifyingGlass />
              </div>
              <p className="rdp-empty-text">Nenhuma devolução encontrada</p>
              <p className="rdp-empty-subtitle">
                Use os filtros acima para buscar
              </p>
            </div>
          ) : null}
        </Container>
      </div>
    </>
  )
}
